import { createEventSource, type EventSourceMessage } from "eventsource-client";
import { API, CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from "@shared/constants";
import { getCookie, refreshAccessToken, tokenHandler } from "@shared/utils";

import {
  IStreamCallbacks,
  IStreamChatReadyEvent,
  IStreamErrorEvent,
  IStreamFinalAnswerEvent,
  IStreamFollowUpsGeneratedEvent,
  IStreamMessageSavedEvent,
  IStreamProgressEvent,
  IStreamQuotaUpdatedEvent,
  IStreamTitleGeneratedEvent,
  IStreamTokenEvent,
  IStreamUsageEvent,
  ISendMessageRequest,
  StreamEventType,
} from "../interfaces";

/** Wire events that end the stream — the client must close() on these or it will auto-reconnect
 * (eventsource-client retries on any disconnect, including a clean stream end, unless told to stop). */
const TERMINAL_EVENTS: ReadonlySet<string> = new Set([StreamEventType.Done, StreamEventType.Error]);

const dispatchEvent = (message: EventSourceMessage, callbacks: IStreamCallbacks): void => {
  if (!message.event) return;

  // Most events carry a JSON payload, but a terminal done/error frame is meaningful even with no
  // (or unparsable) data — never let a missing/malformed payload swallow the event that closes the UI.
  let data: unknown = {};
  if (message.data) {
    try {
      data = JSON.parse(message.data);
    } catch {
      data = {};
    }
  }

  switch (message.event) {
    case StreamEventType.Token:
      callbacks.onToken((data as IStreamTokenEvent).token);
      break;
    case StreamEventType.Progress: {
      const progress = data as IStreamProgressEvent;
      callbacks.onProgress(progress.step, progress.displayMessage);
      break;
    }
    case StreamEventType.FinalAnswer:
      callbacks.onFinalAnswer?.(data as IStreamFinalAnswerEvent);
      break;
    case StreamEventType.MessageSaved:
      callbacks.onMessageSaved?.(data as IStreamMessageSavedEvent);
      break;
    case StreamEventType.ChatReady:
      callbacks.onChatReady?.(data as IStreamChatReadyEvent);
      break;
    case StreamEventType.TitleGenerated:
      callbacks.onTitleGenerated?.((data as IStreamTitleGeneratedEvent).title);
      break;
    case StreamEventType.FollowUpsGenerated:
      callbacks.onFollowUpsGenerated?.(data as IStreamFollowUpsGeneratedEvent);
      break;
    case StreamEventType.Usage:
      callbacks.onUsage?.((data as IStreamUsageEvent).usage);
      break;
    case StreamEventType.QuotaUpdated:
      callbacks.onQuotaUpdated?.(data as IStreamQuotaUpdatedEvent);
      break;
    case StreamEventType.Done:
      callbacks.onDone();
      break;
    case StreamEventType.Error:
      callbacks.onError(new Error((data as IStreamErrorEvent).message ?? "Unknown error"));
      break;
    default:
      break; // unrecognized / forward-compatible event — ignore, never crash the stream
  }
};

/**
 * Streams a cited RAG answer over Server-Sent Events (POST /chat/ask). This bypasses the shared
 * axios client — SSE needs a raw fetch-based transport — so it manually attaches what axios's
 * request interceptor would otherwise add: the bearer token and, since /chat/ask is a mutating POST
 * the backend's CSRF middleware enforces on every non-safe method, the X-CSRF-Token header plus
 * `credentials: "include"` so the CSRF cookie actually reaches the cross-origin API. Without both,
 * the first request 403s with INVALID_CSRF_TOKEN before a single token is ever streamed. A 401 (an
 * expired access token) gets one silent refresh-and-retry via the custom `fetch` below, sharing the
 * same single-flight refresh the axios interceptor uses rather than duplicating it.
 *
 * @param payload - The message (and optional existing chat_uuid) to send.
 * @param callbacks - Per-event handlers; onToken/onProgress/onDone/onError are required.
 * @returns A cleanup function that closes the stream (call on unmount / navigation away).
 */
export const streamMessage = (payload: ISendMessageRequest, callbacks: IStreamCallbacks): (() => void) => {
  const token = tokenHandler.get();
  if (!token) {
    throw new Error("Authentication required");
  }

  const csrfToken = getCookie(CSRF_COOKIE_NAME);

  // eventsource-client's own read loop calls scheduleReconnect() unconditionally on ANY stream
  // EOF — including the normal end of a stream that already delivered a clean done/error frame
  // and was intentionally closed below — so onScheduleReconnect fires after every successful
  // completion too, not just a genuine dropped connection (verified against the library's actual
  // source: onFetchResponse's read loop calls scheduleReconnect() whenever reader.read() reports
  // done, with no check for whether the stream ended cleanly). Track whether we've already reached
  // a terminal outcome so that expected post-close call is a no-op; only a reconnect that fires
  // BEFORE any terminal event was ever seen is a real disconnect worth surfacing.
  let terminalReached = false;

  // Surfaces a non-OK HTTP response (any status other than a successfully-retried 401) through
  // the normal error path: onError + close(). Shared by the first attempt and the post-refresh
  // retry so a 401 that survives the retry (not refreshed again, per streamMessage's contract)
  // is reported exactly like any other error status (403, 500, ...).
  const surfaceHttpError = async (response: Response): Promise<void> => {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    terminalReached = true;
    callbacks.onError(new Error((body as { message?: string }).message ?? `HTTP ${response.status}`));
    eventSource.close();
  };

  const eventSource = createEventSource({
    url: API.CHAT.ASK_QUESTION,
    method: "POST",
    credentials: "include", // send the CSRF cookie cross-origin, matching axios's withCredentials
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
    },
    body: JSON.stringify(payload),
    // eventsource-client has no built-in onError and auto-reconnects by default; detect a failed
    // request here and close() immediately so a bad-auth/over-quota response never silently retries.
    //
    // A 401 gets one silent retry first: this bypasses axios entirely (see the module doc above),
    // so it shares the SAME single-flight, cross-tab-locked refresh the axios interceptor uses
    // (`refreshAccessToken`, exported from request.tsx) rather than a second, duplicated one. The
    // 401 is detected here, before eventsource-client ever reads the body as an event stream, so
    // no token/message event can have been dispatched yet -- retrying never risks a duplicated
    // message.
    fetch: async (url, init) => {
      const response = await fetch(url, init);
      if (response.status !== 401) {
        if (!response.ok) await surfaceHttpError(response);
        return response;
      }

      let newToken: string;
      try {
        newToken = await refreshAccessToken();
      } catch (refreshError) {
        // A failed refresh ends the session unconditionally -- refreshAccessToken() itself clears the
        // token and redirects to login, whether or not the stream below was stopped. What the aborted
        // check skips is only THIS stream's own callbacks (onError + close()): if the stream was
        // stopped while the refresh was in flight, nobody is waiting on them any more, so this just
        // avoids firing a callback after the caller walked away -- it does not affect whether the
        // session ends, which has already happened by this point either way.
        if (!init?.signal?.aborted) {
          terminalReached = true;
          callbacks.onError(refreshError instanceof Error ? refreshError : new Error("Session expired"));
          eventSource.close();
        }
        return response;
      }

      // Stopped while the refresh was in flight: don't fire a retry (or any callback) nobody is
      // waiting on any more.
      if (init?.signal?.aborted) return response;

      // Single retry only -- a 401 on this retried request falls straight into the `!ok` branch
      // above (it is NOT 401-handled again), since this whole block only runs once per fetch call.
      // The refresh also rotates the CSRF cookie, so the retry re-reads it: the header captured above
      // no longer matches the cookie, and the api would reject the retry with INVALID_CSRF_TOKEN (403).
      const refreshedCsrfToken = getCookie(CSRF_COOKIE_NAME);
      const retried = await fetch(url, {
        ...init,
        headers: {
          ...init?.headers,
          Authorization: `Bearer ${newToken}`,
          ...(refreshedCsrfToken ? { [CSRF_HEADER_NAME]: refreshedCsrfToken } : {}),
        },
      });
      if (!retried.ok) await surfaceHttpError(retried);
      return retried;
    },
    onMessage: (message) => {
      dispatchEvent(message, callbacks);
      if (message.event && TERMINAL_EVENTS.has(message.event)) {
        terminalReached = true;
        eventSource.close();
      }
    },
    // This is a one-shot request/response stream, not a persistent feed — a disconnect that never
    // reached a terminal frame (proxy/idle timeout, dropped connection) must surface as an error, not
    // silently retry: a reconnect would re-POST the same body and re-ask the same question.
    onScheduleReconnect: () => {
      if (terminalReached) return;
      terminalReached = true;
      callbacks.onError(new Error("Connection lost"));
      eventSource.close();
    },
  });

  return () => eventSource.close();
};
